import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';

import { refreshSession } from '../../api/auth-fetch';
import { auth, companies } from '../../api/endpoints';
import { m } from 'motion/react';

import { AbsorMark } from '../../components/Brand';
import { Button } from '../../components/Button';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Field } from '../../components/Field';
import { fieldStyles } from '../../components/styles';
import { useT } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { slugify } from '../../lib/slug';
import { useAuth } from '../../stores/auth';
import { useSubmit } from '../auth/useSubmit';

export function CompanyWizard() {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { busy, error, fields, submit } = useSubmit<void>();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const description = String(form.get('description') ?? '');
    const industry = String(form.get('industry') ?? '');
    const mission = String(form.get('mission') ?? '');

    void submit(async () => {
      const company = await companies.create({
        name,
        slug: slug || slugify(name),
        ...(description ? { description } : {}),
        ...(industry ? { industry } : {}),
        ...(mission ? { mission } : {}),
      });

      // **The refresh is required, not a nicety.** The `companies` claim of the
      // token in hand was minted before this company existed, and `14 §5.2` puts
      // the tenant filter on that claim — so without a fresh token the very next
      // request for this company answers 404. See `12 §3`: creation returns the
      // company, never a token pair.
      await refreshSession();
      useAuth.getState().setUser(await auth.me());
      await queryClient.invalidateQueries({ queryKey: ['companies'] });

      await navigate({ to: '/c/$companySlug', params: { companySlug: company.slug } });
    });
  };

  return (
    <m.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.22, 0.8, 0.28, 1] }}
      className="mx-auto max-w-lg space-y-6"
    >
      <div>
        <AbsorMark className="mb-4 size-10" />
        <h1 className="text-2xl font-semibold tracking-tight text-ink">{t('companyWizard.title')}</h1>
        <p className="mt-2 text-sm leading-relaxed text-ink-soft">{t('companyWizard.intro')}</p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        {error ? <ErrorBanner error={error} /> : null}

        <Field
          label={t('companyWizard.name')}
          name="name"
          required
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            if (!slugTouched) {
              setSlug(slugify(event.target.value));
            }
          }}
          error={fields.name}
        />

        <Field
          label={t('companyWizard.slug')}
          name="slug"
          required
          value={slug}
          onChange={(event) => {
            setSlugTouched(true);
            setSlug(event.target.value);
          }}
          hint={t('companyWizard.slugHint')}
          error={fields.slug}
        />

        <Field
          label={t('companyWizard.industry')}
          name="industry"
          hint={t('companyWizard.industryHint')}
          error={fields.industry}
        />

        <div className="space-y-1.5">
          <label htmlFor="mission" className="block text-sm font-semibold text-ink">
            {t('companyWizard.mission')}
          </label>
          <textarea id="mission" name="mission" rows={3} className={cn(fieldStyles, 'resize-y border-line')} />
          <p className="text-xs leading-relaxed text-ink-soft">{t('companyWizard.missionHint')}</p>
        </div>

        <Button type="submit" busy={busy} size="lg" block>
          {t('companyWizard.submit')}
        </Button>
      </form>
    </m.div>
  );
}
